import { Box, Button, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ButtonBaseDemoRoutes from "../../buttonUserIdentificationFirstViewRoutes";
import { colors } from "../../ui/colors";
import { AdminInviteSignup } from "../admin/AdminInviteSignup";
import { SignUp } from "../../../features/SignUp";

export const SignupEntry = () => {
  const [cardVisible, setCardVisible] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);

  const [searchParams] = useSearchParams();
  const invitationUid = useMemo(() => (searchParams.get("invitationUid") || "").trim(), [searchParams]);

  const location = useLocation();
  const [googleUserInfo, setGoogleUserInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t1 = setTimeout(() => setCardVisible(true), 120);
    const t2 = setTimeout(() => setContentVisible(true), 240);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    if (location.state?.userInfo) {
      setGoogleUserInfo(location.state.userInfo);
    }
  }, [location.state]);

  if (invitationUid) {
    return <AdminInviteSignup invitationUid={invitationUid} googleUserInfo={googleUserInfo} />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "var(--fx-surface-base, #FFFFFF)",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 6 },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "var(--fx-surface-base, #FFFFFF)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: "1200px",
          borderRadius: "32px",
          backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.95)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(var(--fx-surface-basergb, 255, 255, 255), 0.2)",
          boxShadow: `
            0 32px 64px rgba(12,12,12,0.08),
            0 16px 32px rgba(251,142,0,0.06),
            inset 0 1px 0 rgba(255,255,255,0.8)
          `,
          p: { xs: 4, md: 8 },
          opacity: cardVisible ? 1 : 0,
          transform: cardVisible ? "translateY(0) scale(1)" : "translateY(30px) scale(0.96)",
          transition: "all 600ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            opacity: contentVisible ? 1 : 0,
            transform: contentVisible ? "translateY(0)" : "translateY(16px)",
            transition: "all 400ms cubic-bezier(0.16, 1, 0.3, 1)",
            transitionDelay: "150ms",
            mb: { xs: 5, md: 8 },
            textAlign: "center",
            position: "relative",
          }}
        >
          <Button
            onClick={() => navigate("/signin")}
            startIcon={<ArrowBack sx={{ fontSize: "1.2rem !important" }} />}
            sx={{
              position: "absolute",
              left: 0,
              top: { xs: "20px", md: "28px" },
              color: colors.gray[400],
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.75rem", md: "0.85rem" },
              minWidth: "auto",
              p: 0,
              "&:hover": {
                color: colors.orange[400],
                backgroundColor: "transparent",
                "& .MuiButton-startIcon": {
                  transform: "translateX(-4px)",
                }
              },
              "& .MuiButton-startIcon": {
                transition: "transform 0.2s",
              },
              zIndex: 10,
            }}
          >
            Go back to Sign In
          </Button>
          <Box
            sx={{
              position: "relative",
              display: "inline-block",
              mb: 3,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                background: `linear-gradient(135deg, ${colors.gray.base}, ${colors.gray[400]})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "48px", md: "64px" },
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                position: "relative",
              }}
            >
              SIGN UP
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box
              sx={{
                height: 6,
                width: { xs: "120px", md: "160px" },
                borderRadius: "999px",
                background: `linear-gradient(90deg, 
                  ${colors.orange[300]}, 
                  ${colors.orange[400]} 30%, 
                  ${colors.yellow[400]} 70%, 
                  ${colors.yellow[300]}
                )`,
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "calc(100% + 4px)",
                  height: "calc(100% + 4px)",
                  borderRadius: "999px",
                  background: `linear-gradient(90deg, 
                    ${colors.orange[200]}, 
                    ${colors.yellow[200]}
                  )`,
                  zIndex: -1,
                  opacity: 0.5,
                },
              }}
            />
          </Box>

          {/* <Typography
            sx={{
              color: colors.gray[400],
              fontSize: { xs: "20px", md: "24px" },
              fontWeight: 500,
              mb: 3,
              maxWidth: "600px",
              mx: "auto",
              lineHeight: 1.4,
            }}
          >
            Choose your account type and start your journey
          </Typography> */}
        </Box>

        {/* Options */}
        <Box
          sx={{
            opacity: contentVisible ? 1 : 0,
            transform: contentVisible ? "translateY(0)" : "translateY(20px)",
            transition: "all 400ms cubic-bezier(0.16, 1, 0.3, 1)",
            transitionDelay: "250ms",
          }}
        >
          {/* <ButtonBaseDemoRoutes userInfo={googleUserInfo} />
          <Typography
            sx={{
              mt: 2,
              borderRadius: "8px",
              padding: "8px",
              textAlign: "center",
              fontSize: "0.6rem",
              fontWeight: 500,
              color: colors.gray[400],
            }}
          >
            <span onClick={() => navigate("/signup/create-user")} style={{ cursor: "pointer" }}>
              Create a user account
            </span>
          </Typography> */}
          <SignUp />
        </Box>
      </Box>
    </Box>
  );
};
